<script setup>
import { ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { SurveyType } from '@/enums/survey/survey.type'
import { VDataTableServer } from 'vuetify/lib/labs/VDataTable/index.mjs'

definePage({
  meta: {
    name: "Customer Survey Collection",
  },
})

const route = useRoute()
const router = useRouter()

const { t } = useI18n()

const header = computed(() => {
  let data = [
    {
      title: 'Actions',
      key: 'actions',
      sortable: false,
    },

    {
      title: `${t('customer')}`,
      key: 'customer',
    },
    {
      title: 'Unit',
      key: 'episode.unit.name',
    },
    {
      title: 'Survey',
      key: 'survey.title',
    },
  ]

  // if (props.survey.type == SurveyType.CUSTOMER) {
  //   data.push(...[
  //     {
  //       title: `${t('customer')}`,
  //       key: 'customer',
  //     },
  //     {
  //       title: 'Unit',
  //       key: 'episode.unit.name',
  //     },
  //   ])
  // }

  // if (props.survey.type == SurveyType.EMPLOYEE || props.survey.type == SurveyType.EVENT) {
  //   data.push(...[
  //     {
  //       title: 'Participant',
  //       key: 'participant',
  //     },
  //     {
  //       title: 'Unit',
  //       key: 'participant.unit.name',
  //     },
  //   ])
  // }

  data.push(...[
    // {
    //   title: 'Schedule At',
    //   key: 'schedule_at',
    // },
    // {
    //   title: 'Collected At',
    //   key: 'collected_at',
    // },

    {
      title: 'Total Schedule',
      key: 'total_schedule',
    },
  ])

  return data
})

const filterSurvey = ref(route.query?.survey ?? null)

const {
  isFetching: fetchingSurveys,
  data: filterSurveys,
  execute: fetchFilterSurveys,
} = useApi(createUrl('/survey/surveys', {
  query: {
    search: {
      type: SurveyType.CUSTOMER,
    },
    select: ['id', 'title'],
  },
}))

const computedFilters = computed(() => {
  if (filterSurvey.value) {
    return {
      survey_id: filterSurvey.value,
    }
  }

  return {}
})

const manualLoading = ref(false)

const searchQuery = ref('')
const itemsPerPage = ref(10)
const pageNumber = ref(1)
const sortBy = ref()
const orderBy = ref()

const {
  isFetching: isFetchingSurveys,
  data: surveysData,
  execute: fetchSurveys,
} = useApi(createUrl('/survey/survey-responses', {
  query: {
    q: searchQuery,
    survey_type: SurveyType.CUSTOMER,
    search: computedFilters,
    paged: 1,
    itemsPerPage,
    page: pageNumber,
    sort: sortBy,
    order_by: orderBy,
  },
}),
{
  afterFetch: ({ data }) => {
    manualLoading.value = false
  },
})

watch(() => computedFilters.value, (newVal, oldVal) => {
  if(newVal.created_at !== oldVal.created_at) {
    manualLoading.value = true
  }
}, { deep: true })

const surveys = computed(() => surveysData.value?.data ?? surveysData.value)
const totalSurveys = computed(() => surveysData.value?.meta?.total ?? surveysData.value?.length)

const updateOptions = options => {
  pageNumber.value = options.page
  orderBy.value = options.sortBy[0]?.key
  sortBy.value = options.sortBy[0]?.order
}

const exportLoading = ref(false)

const downloadSurveyResponse = async () => {
  exportLoading.value = true

  const url = createUrl('survey/survey-responses/export', {
    query: {
      q: searchQuery,
      search: computedFilters,
      sort: sortBy,
      order_by: orderBy,
    },
  })

  await $api(url.value).then(async () => {
    await new Promise(resolve => setTimeout(resolve, 2000))
  }).catch(error => {})

  exportLoading.value = false
}
</script>

<template>
  <div class="d-flex flex-column gap-y-6">
    <!-- Table -->
    <VCard
      variant="elevated"
      elevation="3"
    >
      <VCardText class="d-flex flex-wrap py-4 gap-4">
        <div
          class="me-3 d-flex gap-3"
          style="inline-size: 16rem;"
        >
          <AppSelect
            v-model="filterSurvey"
            :loading="fetchingSurveys"
            :items="filterSurveys ?? []"
            :item-title="item => item.title"
            :item-value="item => item.id"
            placeholder="Filter By Survey"
            :return-object="false"
            clearable
          />
        </div>
        <VSpacer />

        <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
          <div style="inline-size: 16rem;">
            <AppTextField
              v-model="searchQuery"
              placeholder="Search"
              density="compact"
            />
          </div>

          <VBtn
            variant="tonal"
            color="primary"
            :loading="exportLoading"
            @click="downloadSurveyResponse"
          >
            Export
          </VBtn>

          <VBtn
            color="secondary"
            variant="tonal"
            @click="fetchSurveys"
          >
            <VIcon
              class="cursor-pointer"
              icon="tabler-refresh"
              size="1.5rem"
            />
          </VBtn>
        </div>
      </VCardText>

      <VDivider />

      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:page="pageNumber"
        :items="surveys ?? []"
        :items-length="totalSurveys"
        :headers="header"
        fixed-header
        class="text-no-wrap"
        :loading="isFetchingSurveys || manualLoading"
        @update:options="updateOptions"
      >
        <template #item.customer="{ item }">
          <span
            v-if="item.episode"
            class="text-capitalize text-primary font-weight-medium cursor-pointer"
          > 
            <VMenu
              open-on-hover
              :close-on-content-click="false"
            >  
              <template #activator="{ props }">
                <VChip v-bind="props">
                  {{ item.episode.customer.name ?? 'N/A' }}
                </VChip>
              </template>
              <VCard max-width="300">
                <VList>
                  <VListItem class="mx-0">
                    <template #title>
                      <VListItemTitle class="text-h6 text-primary text-capitalize">
                        {{ item.episode.customer.name ?? 'N/A' }}
                      </VListItemTitle>
                    </template>
                  </VListItem>
                </VList>
      
                <VDivider />
                <VList>
                  <VListItem class="mx-0 p-0">
                    <template v-if="item.episode.customer.uhid">
                      <VListItemTitle>
                        UHID:
                        {{ item.episode.customer.uhid }}
                      </VListItemTitle>
                    </template>
                  
                    <template v-if="item.episode.customer.phone">
                      <VListItemTitle>
                        Phone:
                        {{ item.episode.customer.phone }}
                      </VListItemTitle>
                    </template>
                    <template v-if="item.episode.customer.email">
                      <VListItemTitle>
                        Email:
                        {{ item.episode.customer.email }}
                      </VListItemTitle>
                    </template>
                  </VListItem>
                </VList>
              </VCard>
            </VMenu>
          </span>
        </template>
        <template #item.participant="{ item }">
          <span
            v-if="item.participant"
            class="text-capitalize text-primary font-weight-medium cursor-pointer"
          > 
            <VMenu
              open-on-hover
              :close-on-content-click="false"
            >  
              <template #activator="{ props }">
                <VChip v-bind="props">
                  {{ item.participant.name ?? 'N/A' }}
                </VChip>
              </template>
              <VCard max-width="300">
                <VList>
                  <VListItem class="mx-0">
                    <template #title>
                      <VListItemTitle class="text-h6 text-primary text-capitalize">
                        {{ item.participant.name ?? 'N/A' }}
                      </VListItemTitle>
                    </template>
                  </VListItem>
                </VList>
      
                <VDivider />
                <VList>
                  <VListItem class="mx-0 p-0">                  
                    <template v-if="item.participant.phone">
                      <VListItemTitle>
                        Phone:
                        {{ item.participant.phone }}
                      </VListItemTitle>
                    </template>
                    <template v-if="item.participant.email">
                      <VListItemTitle>
                        Email:
                        {{ item.participant.email }}
                      </VListItemTitle>
                    </template>
                  </VListItem>
                </VList>
              </VCard>
            </VMenu>
          </span>
        </template>
        <template #item.schedule_at="{ item }">
          {{ formatDate(item.schedule_at) || 'N/A' }}
        </template>
        <template #item.triggered_at="{ item }">
          {{ formatDate(item.triggered_at) || 'N/A' }}
        </template>
        <template #item.total_schedule="{ item }">
          <VChip>
            {{ item.total }} Schedule
          </VChip>
        </template>
        <template #item.actions="{ item }">
          <!--
            <IconBtn>
            <VIcon icon="tabler-eye" />
            </IconBtn> 
          -->
          <IconBtn @click="router.push({ name: 'app-survey-collection-customer-view', params: { survey: item.survey_id, type: item.survey.type, participant: item.episode_id } })">
            <VIcon icon="tabler-notes" />
          </IconBtn>
        </template>
      </VDataTableServer>
    </VCard>
  </div>
</template>
